import { type AxiosResponse } from "axios";

import useTeamPicker from "shared/composables/useTeamPicker";
import features from "shared/services/features";
import type { Extends } from "shared/types";
import type {
  MentionWithSyndication,
  PodcastEpisode,
  RadioClip,
  TvCaption,
  TvClip,
  TvLogoAppearance,
  TvSuper,
} from "shared/types/mentions";

import ApiClient from "./ApiClient";

type TranscriptRequestMentionsWithSyndication = Extends<
  MentionWithSyndication,
  TvCaption | TvSuper | RadioClip
>;

export type TranscriptRequestMention =
  | TranscriptRequestMentionsWithSyndication
  | TvLogoAppearance
  | TvClip
  | PodcastEpisode;

// note this is not a complete type
export type TranscriptDuplicate = {
  id: number;
  type: string;
};

class TranscriptRequestService extends ApiClient {
  constructor() {
    super("/transcript_requests");
  }

  cancel(id: number): Promise<AxiosResponse<"">> {
    return this.put<"">(`/${id}/cancel`);
  }

  findDuplicates(
    mention: TranscriptRequestMention,
    { seconds }: { seconds: number }
  ): Promise<TranscriptDuplicate[]> {
    const source = [mention.source.id];

    if ("syndications" in mention) {
      source.push(
        ...mention.syndications.map((syndication) => syndication.source_id)
      );
    }

    const timestamp = mention.timestamp || (mention as TvCaption).start_time;

    const { selectedTeamPreference } = useTeamPicker();

    const params = {
      podcast_episode_id:
        mention.type === "podcast_episode" ? mention.id : null,
      source,
      start_after: timestamp - seconds,
      start_before: timestamp + seconds,
      type: this.mentionType(mention),
      ...(features.has("has_team_workspaces")
        ? { organisation_team_id: selectedTeamPreference.value }
        : {}),
    };

    return this.post<TranscriptDuplicate[]>("/find_duplicates", {
      params,
    }).then(({ data }) => data);
  }

  mentionType(mention: TranscriptRequestMention): string {
    if (mention.type === "podcast_episode") {
      return "Podcast";
    }

    if (mention.type === "radio_clip") {
      return "RadioStation";
    }

    return "TvChannel";
  }
}

export default new TranscriptRequestService();
